/**
 * Library — the "Reading Room" treatment for catalog pages.
 *
 * Renders pageData.library (emitted by scripts/build_education_page.mjs):
 * a card-catalog of subject drawers above the fold, a librarian's-desk
 * filter, then one shelf per subject whose course groups are collapsible
 * drawers that open onto aged-paper card trays. All records are in the
 * static HTML (SEO/no-JS safe); disclosure + filtering tame them visually.
 * Generic: any page whose dir carries a library.json gets this treatment.
 */
import React, { useDeferredValue, useMemo, useState } from 'react';
import styles from './Library.module.css';

export interface LibraryDoc { title: string; href: string; path: string; pages: number | null; }
export interface LibraryGroup { name: string; count: number; docs: LibraryDoc[]; }
export interface LibrarySubject { id: string; subject: string; count: number; note?: string; groups: LibraryGroup[]; }
export interface LibraryTool { title: string; href: string; note?: string; }
export interface LibraryData { generated?: string; total: number; subjects: LibrarySubject[]; tools?: LibraryTool[]; }

const norm = (s: string) => s.toLowerCase();

function docMatches(d: LibraryDoc, group: string, subject: string, q: string): boolean {
  return norm(`${d.title} ${d.path} ${group} ${subject}`).includes(q);
}

export const Library: React.FC<{ data: LibraryData }> = ({ data }) => {
  const [query, setQuery] = useState('');
  const q = norm(useDeferredValue(query).trim());
  const filtering = q.length >= 2;

  const shown = useMemo(() => {
    if (!filtering) return data.total;
    let n = 0;
    for (const s of data.subjects)
      for (const g of s.groups)
        for (const d of g.docs) if (docMatches(d, g.name, s.subject, q)) n++;
    return n;
  }, [data, q, filtering]);

  const nGroups = data.subjects.reduce((n, s) => n + s.groups.length, 0);

  return (
    <section className={styles.library} aria-label="Document catalog">
      {/* ── card catalog ── */}
      <div className={styles.eyebrow} id="card-catalog">Card Catalog</div>
      <nav className={styles.catalog} aria-label="Subjects">
        {data.subjects.map((s) => (
          <a key={s.id} className={styles.drawerFace} href={`#${s.id}`}>
            <span className={styles.pull} aria-hidden="true" />
            <span className={styles.plate}>{s.subject}</span>
            <span className={styles.plateCount}>
              {s.count} {s.count === 1 ? 'doc' : 'docs'}
              {s.id === 'uncatalogued' ? <em className={styles.stampChip}> · uncatalogued</em> : null}
            </span>
          </a>
        ))}
      </nav>

      {/* ── librarian's desk ── */}
      <div className={styles.desk}>
        <input
          className={styles.filter}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${data.total} records — title, course, subject…`}
          aria-label="Filter catalog records"
        />
        <output className={styles.count} aria-live="polite">
          {filtering ? `${shown} of ${data.total} records` : `${data.total} records`}
        </output>
        <span className={styles.stats}>
          {data.subjects.length} subjects · {nGroups} course groups
        </span>
      </div>

      {/* ── shelves ── */}
      {data.subjects.map((s) => {
        const subjectHasMatch = filtering && s.groups.some((g) =>
          g.docs.some((d) => docMatches(d, g.name, s.subject, q)));
        if (filtering && !subjectHasMatch) return null;
        return (
          <section key={s.id} className={styles.shelf} aria-labelledby={`h-${s.id}`}>
            <div className={styles.shelfHead}>
              <h2 id={s.id} className={styles.shelfTitle}>
                <span id={`h-${s.id}`}>{s.subject}</span>
              </h2>
              <span className={styles.shelfCount}>{s.count}</span>
              <a className={styles.backlink} href="#card-catalog">↑ catalog</a>
            </div>
            {s.note ? <p className={styles.shelfNote}>{s.count} {s.note}</p> : null}
            {s.groups.map((g) => {
              const matches = filtering
                ? g.docs.filter((d) => docMatches(d, g.name, s.subject, q))
                : g.docs;
              if (filtering && !matches.length) return null;
              return (
                <details
                  key={`${filtering ? 'f' : 'r'}-${s.id}-${g.name}`}
                  className={styles.drawer}
                  open={filtering || s.count <= 20}
                >
                  <summary className={styles.drawerSummary}>
                    <span className={styles.chevron} aria-hidden="true" />
                    <span className={styles.drawerName}>{g.name}</span>
                    <span className={styles.drawerCount}>
                      {filtering ? `${matches.length} of ${g.count}` : g.count}
                    </span>
                  </summary>
                  <ol className={styles.tray}>
                    {matches.map((d) => (
                      <li key={d.href} className={styles.card}>
                        <span className={styles.cardLine}>
                          <a className={styles.cardTitle} href={d.href}>{d.title}</a>
                          <span className={styles.leader} aria-hidden="true" />
                          {d.pages ? <span className={styles.pp}>{d.pages} pp</span> : null}
                        </span>
                        {d.path ? (
                          <span className={styles.cardPath} title={d.path}>{d.path}</span>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                </details>
              );
            })}
          </section>
        );
      })}

      {/* ── reference desk ── */}
      {data.tools?.length ? (
        <div className={styles.reference}>
          <div className={styles.eyebrow}>Reference Desk</div>
          {data.tools.map((t) => (
            <a key={t.href} className={styles.toolCard} href={t.href}
               target="_blank" rel="noopener noreferrer">
              <span className={styles.toolTitle}>{t.title}</span>
              {t.note ? <span className={styles.toolNote}>{t.note}</span> : null}
            </a>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default Library;
